import React from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { GitBranch, TrendingDown, Fish, BarChart3, Zap, ClipboardList } from 'lucide-react';

const RCAToolsHub: React.FC = () => {
    const { incidentId } = useParams<{ incidentId?: string }>();

    interface RCATool {
        name: string;
        icon: React.ElementType;
        description: string;
        color: string;
        link: string;
        features: string[];
        comingSoon?: boolean;
    }

    const tools: RCATool[] = [
        {
            name: '5-Why Analysis',
            icon: TrendingDown,
            description: 'Drill down to root causes by asking "Why?" up to 5 times',
            color: 'bg-blue-500',
            link: incidentId ? `/rca/5why/${incidentId}` : '/rca/5why',
            features: ['Simple & effective', 'Quick to complete', 'Iterative questioning'],
        },
        {
            name: 'Ishikawa Diagram',
            icon: Fish,
            description: 'Identify causes across 6 key categories (6M Method)',
            color: 'bg-purple-500',
            link: incidentId ? `/rca/ishikawa/${incidentId}` : '/rca/ishikawa',
            features: ['Systematic approach', 'Team collaboration', 'Visual diagram'],
        },
        {
            name: 'Pareto Analysis',
            icon: BarChart3,
            description: 'Identify the vital few causes (80/20 rule)',
            color: 'bg-green-500',
            link: '/analytics',
            features: ['Data-driven', 'Prioritization', 'Chart visualization'],
        },
        {
            name: 'Fault Tree Analysis',
            icon: GitBranch,
            description: 'Logical analysis of failure combinations',
            color: 'bg-orange-500',
            link: '/analytics/fta',
            features: ['Boolean logic', 'System failures', 'Probability analysis'],
        },
    ];

    return (
        <div className="p-8 max-w-7xl mx-auto">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Root Cause Analysis Tools
                </h1>
                <p className="text-gray-600">
                    Systematic methods to identify and verify root causes of failures
                </p>
            </div>

            {/* Tool Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
                {tools.map((tool, index) => {
                    const Icon = tool.icon;
                    return (
                        <Link
                            key={index}
                            to={tool.link}
                            className={`bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-blue-500 hover:shadow-lg transition-all ${tool.comingSoon ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                                }`}
                            onClick={(e) => tool.comingSoon && e.preventDefault()}
                        >
                            <div className="flex items-start gap-4">
                                <div className={`w-16 h-16 rounded-lg ${tool.color} flex items-center justify-center flex-shrink-0`}>
                                    <Icon className="w-8 h-8 text-white" />
                                </div>

                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h3 className="text-xl font-semibold text-gray-900">
                                            {tool.name}
                                        </h3>
                                        {tool.comingSoon && (
                                            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                                Coming Soon
                                            </span>
                                        )}
                                    </div>

                                    <p className="text-gray-600 mb-4">
                                        {tool.description}
                                    </p>

                                    <div className="flex flex-wrap gap-2">
                                        {tool.features.map((feature, idx) => (
                                            <span
                                                key={idx}
                                                className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                                            >
                                                {feature}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </Link>
                    );
                })}

                {/* Methodology Templates Card */}
                <Link
                    to="/rca/templates"
                    className="md:col-span-2 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-6 hover:shadow-xl transition-all group"
                >
                    <div className="flex items-center gap-6">
                        <div className="w-20 h-20 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                            <ClipboardList className="w-10 h-10 text-white" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-2xl font-bold text-white mb-2">Methodology Templates</h3>
                            <p className="text-blue-100 mb-4 max-w-2xl">
                                Access standardized frameworks for enterprise-grade problem solving:
                                DMAIC, PDCA, A3 Problem Solving, and 4D Analysis.
                            </p>
                            <div className="flex gap-4">
                                <span className="px-3 py-1 bg-white/20 text-white text-xs font-semibold rounded-full backdrop-blur-sm">DMAIC</span>
                                <span className="px-3 py-1 bg-white/20 text-white text-xs font-semibold rounded-full backdrop-blur-sm">PDCA</span>
                                <span className="px-3 py-1 bg-white/20 text-white text-xs font-semibold rounded-full backdrop-blur-sm">A3</span>
                                <span className="px-3 py-1 bg-white/20 text-white text-xs font-semibold rounded-full backdrop-blur-sm">4D Analysis</span>
                            </div>
                        </div>
                        <Zap className="w-12 h-12 text-amber-400 opacity-20 group-hover:opacity-100 transition-opacity" />
                    </div>
                </Link>
            </div>

            {/* Best Practices */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                    <Zap className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                        <h3 className="text-lg font-semibold text-blue-900 mb-2">
                            Best Practices for Root Cause Analysis
                        </h3>
                        <ul className="space-y-2 text-blue-800">
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 mt-1">•</span>
                                <span><strong>Use Multiple Methods:</strong> Combine 5-Why with Ishikawa for comprehensive analysis</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 mt-1">•</span>
                                <span><strong>Involve the Team:</strong> Diverse perspectives lead to better root cause identification</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 mt-1">•</span>
                                <span><strong>Verify with Data:</strong> Always back up your hypotheses with evidence</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 mt-1">•</span>
                                <span><strong>Focus on Systems:</strong> Look beyond individual errors to systemic issues</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 mt-1">•</span>
                                <span><strong>Document Everything:</strong> Create a trail for future reference and learning</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RCAToolsHub;
